import type { ReactNode } from 'react'
import { Link } from 'react-router'
import styles from './ChoiceList.module.css'

export interface ChoiceProps {
  /** Leading icon — a Nexus icon at size 18 reads best. */
  icon?: ReactNode
  label: ReactNode
  /** One line under the label saying what picking this means. */
  hint?: ReactNode
  /** Navigate on pick. With neither this nor `onClick`, the row is information only. */
  to?: string
  onClick?: () => void
  disabled?: boolean
}

/**
 * One way forward. Renders a router link when `to` is set, a button when only
 * `onClick` is, so a choice that leaves the page is still a real link
 * (middle-click, copy). With neither it is a plain row — something to do that
 * no link can take you to ("play more ranked matches") — and it does not
 * pretend to be clickable.
 */
export function Choice({ icon, label, hint, to, onClick, disabled = false }: ChoiceProps) {
  const body = (
    <>
      {icon && <span className={styles.icon}>{icon}</span>}
      <span className={styles.text}>
        <span className={styles.label}>{label}</span>
        {hint && <span className={styles.hint}>{hint}</span>}
      </span>
    </>
  )

  if (!to && !onClick) {
    return (
      <li>
        <div className={`${styles.choice} ${styles.static}`}>{body}</div>
      </li>
    )
  }

  // A disabled link is not a thing; it becomes a disabled button.
  if (to && !disabled) {
    return (
      <li>
        <Link to={to} onClick={onClick} className={styles.choice}>{body}</Link>
      </li>
    )
  }
  return (
    <li>
      <button type="button" className={styles.choice} onClick={onClick} disabled={disabled}>
        {body}
      </button>
    </li>
  )
}

export interface ChoiceListProps {
  children: ReactNode
  /** Names the list for screen readers, e.g. "How do you want to enter?". */
  'aria-label'?: string
  className?: string
}

/** A short stack of mutually exclusive ways forward — each one a full-width row. */
export function ChoiceList({ children, className, ...props }: ChoiceListProps) {
  return (
    <ul className={[styles.list, className].filter(Boolean).join(' ')} aria-label={props['aria-label']}>
      {children}
    </ul>
  )
}
